/*
 * $Id:: UserDetailsCard.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';

import { GLOBALS_SLICE_NAME } from '../../../../app/constants';
import { countries as countriesSlice, states as statesSlice } from '../../../../app/globalsSlice';
import inputsMaxChars from '../../../../app/inputsMaxChars';
import { ReactComponent as DropDownArrow } from '../../../../assets/images/unfold_more_24px.svg';
import Card from '../../../../components/Card';
import ErrorInput from '../../../../components/ErrorInput';
import { t } from '../../../../providers';
import { loadingUserDetails } from '../../accountSlice';

const useStyles = makeStyles((theme) => ({
    fieldLabel: {
        color: theme.palette.text.label,
        fontSize: '0.625rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 'normal',
    },
    fieldValue: {
        color: theme.palette.text.value,
        fontSize: '0.75rem',
        letterSpacing: 'normal',
        marginBottom: '10px',
    },
    linkValue: {
        color: theme.palette.text.linkBlue,
        fontSize: '0.75rem',
        letterSpacing: 'normal',
        marginBottom: '10px',
        textDecoration: 'underline',
    },
    textInput: {
        height: '2rem',
        width: '9rem',
        borderRadius: '2px',
        marginTop: '5px',
        marginBottom: '10px',
        '@media (max-width:600px)': {
            width: '9.5rem',
        },
    },
    selectWidth: {
        width: '12rem',
    },
    selectSmallWidth: {
        width: '11rem',
    },
    corrAddressTitle: {
        marginLeft: '-10px',
        marginBottom: '24px',
        marginTop: '20px',
    },
    inputError: {
        marginTop: '-5px',
        marginBottom: '5px',
    },
    disabledMenuItem: {
        color: '#5D5D5D',
        opacity: '1 !important',
    },
}));

// Applicant - Individual
const applicantIndividualFields = ['firstName', 'surName', 'nationality', 'vatNumber'];

// Applicant - Business / Company
const applicantBusinessFields = ['legalName', 'legalForm', 'incorporationCountry', 'incorporationState', 'vatNumber'];

const repLegalPractFields = (legalStatus) => {
    const nameFields = legalStatus === 'Individual(s)' ? ['firstName', 'surName'] : ['organizationName'];
    return [...nameFields, 'nationality'];
};

// Representative - Employee Representative
const repEmployeeRepFields = (legalStatus) => {
    const nameFields = legalStatus === 'Individual(s)' ? ['firstName', 'surName'] : ['legalName'];
    const fields = [...nameFields, 'nationality', 'economicConnection'];
    return [...fields, 'economicNature', 'employerName'];
};

const user2FieldsMapping = {
    Applicant: {
        'Individual(s)': () => applicantIndividualFields,
        'Business/Company': () => applicantBusinessFields,
    },
    Representative: {
        'Employee Representative': repEmployeeRepFields,
        'Legal Practitioner': repLegalPractFields,
        'EUIPO Professional Practitioner': repLegalPractFields,
        Association: () => ['organizationName', 'nationality'],
    },
};

const getFieldsByUser = ({ accountTypeDetails = {} }) => {
    const { personType, accountType, legalStatus } = accountTypeDetails;
    const hasTypes = !!personType && !!accountType;
    return hasTypes ? user2FieldsMapping[personType][accountType](legalStatus) : [];
};

const UserDetailsCard = ({ initialValues, saveDetails, hasAdministratorRole }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const countries = useSelector(countriesSlice);
    const loading = useSelector(loadingUserDetails);
    const states = useSelector(statesSlice);

    const userFields = getFieldsByUser(initialValues);

    const getCountryOrStateName = (code, options) => {
        const country = options.find((c) => c.code === code) || {};
        return country.value || code;
    };

    const [readonly, setReadonly] = useState(true);

    const [userDetails, setUserDetails] = useState({});

    const [triggerInitialData, setTriggerInitialData] = useState(false); // trigger to get initial data

    useEffect(() => {
        if (!countries || !countries.length) dispatch({ type: `${GLOBALS_SLICE_NAME}/fetchCountries` });

        const mainAccountDetails = (initialValues && initialValues.mainAccountDetails) || {};
        const eConnectionsOptions = [
            { value: 'Yes', code: 'Yes' },
            { value: 'No', code: 'No' },
        ];
        const isAssociation = initialValues.accountTypeDetails && initialValues.accountTypeDetails.accountType === 'Association';
        const nationalityLabel = isAssociation ? 'representative.country.origin' : 'nationality';

        const userPersonType = initialValues && initialValues.accountTypeDetails && initialValues.accountTypeDetails.personType;
        const userLegalStatus = initialValues && initialValues.accountTypeDetails && initialValues.accountTypeDetails.legalStatus;
        const isRepresentativeBussiness = userPersonType === 'Representative' && userLegalStatus === 'Business/Company';

        const newUserDetails = {
            firstName: {
                value: mainAccountDetails.firstName || '',
                placeholder: 'type.first.name',
                label: 'first.name',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.firstName,
            },
            surName: {
                value: mainAccountDetails.surName || '',
                placeholder: isRepresentativeBussiness ? 'type.name' : 'type.your.surname',
                label: isRepresentativeBussiness ? 'name' : 'surname',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.lastName,
            },
            nationality: {
                value: mainAccountDetails.nationality || '',
                placeholder: 'select.nationality',
                label: nationalityLabel,
                isDirty: false,
                element: 'Select',
                options: countries,
                required: true,
            },
            organizationName: {
                value: mainAccountDetails.organizationName || '',
                placeholder: 'type.name',
                label: 'organisation.name',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.organisationName,
            },
            legalName: {
                value: mainAccountDetails.legalName || '',
                placeholder: 'type.legal.name',
                label: 'legal.name',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.legalName,
            },
            legalForm: {
                value: mainAccountDetails.legalForm || '',
                placeholder: 'type.legal.form',
                label: 'legal.form',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.legalForm,
            },
            incorporationCountry: {
                value: mainAccountDetails.incorporationCountry || '',
                placeholder: 'select.country',
                label: 'country.registration',
                isDirty: false,
                element: 'Select',
                options: countries,
                required: true,
            },
            incorporationState: {
                value: mainAccountDetails.incorporationState || '',
                placeholder: 'select.state',
                label: 'state.incorporation',
                isDirty: false,
                element: 'Select',
                noIterrate: !mainAccountDetails.incorporationState,
            },
            vatNumber: {
                value: mainAccountDetails.vatNumber || '',
                placeholder: 'type.vat.number',
                label: 'vat.number',
                isDirty: false,
                element: 'Input',
                maxChars: inputsMaxChars.vatCode,
            },
            economicConnection: {
                value: (!!mainAccountDetails.economicConnection && 'Yes') || 'No',
                placeholder: 'yes.no',
                label: 'is.representation.on.economic',
                isDirty: false,
                element: 'Select',
                options: eConnectionsOptions,
            },
            employerName: {
                value: mainAccountDetails.employerName || '',
                placeholder: 'type.name.employer',
                label: 'name.employer',
                isDirty: false,
                element: 'Input',
                noIterrate: !mainAccountDetails.economicConnection,
                maxChars: inputsMaxChars.employerName,
            },
            economicNature: {
                value: mainAccountDetails.economicNature || '',
                placeholder: 'type.nature.connections',
                label: 'nature.economic.connections',
                isDirty: false,
                element: 'Input',
                noIterrate: !mainAccountDetails.economicConnection,
                maxChars: inputsMaxChars.economicNature,
            },
        };

        let finalUserDetails = { ...newUserDetails };

        /* employer name must be required if economicConnection's value is Yes */
        if (finalUserDetails.economicConnection && finalUserDetails.economicConnection.value === 'Yes') {
            finalUserDetails.economicNature.noIterrate = finalUserDetails.economicConnection.value !== 'Yes';
            finalUserDetails.employerName.noIterrate = finalUserDetails.economicConnection.value !== 'Yes';
            if (finalUserDetails.economicConnection.value === 'No') {
                finalUserDetails.economicNature.value = '';
                finalUserDetails.employerName.value = '';
                finalUserDetails.employerName.required = false;
                finalUserDetails.employerName.errorMessage = null;
            } else {
                finalUserDetails.employerName.required = true;
                finalUserDetails.employerName.errorMessage = null;
            }
        }

        /* show states's field, if the selected incorporationCountry has states */
        if (finalUserDetails.incorporationCountry && finalUserDetails.incorporationCountry.value) {
            const statesOfCountry = !!states && states.find((s) => s.countryCode === finalUserDetails.incorporationCountry.value);
            const statesOptions = (!!statesOfCountry && finalUserDetails.state) || [];
            finalUserDetails.incorporationState.noIterrate = !statesOfCountry;
            finalUserDetails.incorporationState.options = statesOfCountry ? statesOptions : [];

            if (!statesOfCountry) finalUserDetails.incorporationState.value = '';
        }

        setUserDetails(finalUserDetails);
        setReadonly(true);
    }, [initialValues, countries, dispatch, states, triggerInitialData]);

    const handleValueChange = (event) => {
        const value = event.target.value;
        const keyName = event.target.name;
        const field = { ...userDetails[keyName], value, isDirty: true };
        if (!field.maxChars || (field.maxChars && value.length <= field.maxChars)) {
            let errorMessage = null;
            if (field.required && !value) errorMessage = 'required.field';
            field.errorMessage = errorMessage;

            let newDetailsValue = { ...userDetails, [keyName]: field };

            if (event.target.name === 'economicConnection') {
                newDetailsValue.economicNature.noIterrate = value !== 'Yes';
                newDetailsValue.employerName.noIterrate = value !== 'Yes';
                if (value === 'No') {
                    newDetailsValue.economicNature.value = '';
                    newDetailsValue.employerName.value = '';
                    newDetailsValue.employerName.required = false;
                    newDetailsValue.employerName.errorMessage = null;
                } else {
                    newDetailsValue.employerName.required = true;
                    newDetailsValue.employerName.errorMessage = null;
                }
            }
            if (event.target.name === 'incorporationCountry') {
                const statesOfCountry = !!states && states.find((s) => s.countryCode === value);
                const statesOptions = (!!statesOfCountry && statesOfCountry.state) || [];
                newDetailsValue.incorporationState.noIterrate = !statesOfCountry;
                newDetailsValue.incorporationState.options = statesOfCountry ? statesOptions : [];
                if (!statesOfCountry) newDetailsValue.incorporationState.value = '';
            }
            setUserDetails(newDetailsValue);
        }
    };

    const cancelCard = (event) => {
        event.preventDefault();

        setTriggerInitialData(!triggerInitialData);
        setReadonly(true);
    };

    const saveCard = (event) => {
        event.preventDefault();

        const newPersonDetails = Object.keys(userDetails).reduce((acc, cur) => ({ ...acc, [cur]: userDetails[cur].value }), {});
        const updatedDetails = Object.keys(userDetails).reduce(
            (acc, cur) => (userDetails[cur].isDirty ? { ...acc, [cur]: userDetails[cur].value } : { ...acc }),
            {}
        );

        // get the available userFields that can be iterrated
        const availableUserFields = Object.fromEntries(Object.entries(userDetails).filter(([field]) => userFields.includes(field)));

        //check for errors in the specific user Fields
        const hasErrors = Object.keys(availableUserFields).some(
            (field) => userDetails[field].errorMessage && userDetails[field].errorMessage !== null
        );

        let availableUserFieldsWithErrorsMessage = { ...availableUserFields };
        const missingRequiredValues = Object.fromEntries(
            Object.entries(availableUserFieldsWithErrorsMessage).filter(([key, fieldObject]) => {
                let result = false;
                if (fieldObject.required && fieldObject.value === '') {
                    availableUserFieldsWithErrorsMessage[key].errorMessage = 'required.field';
                    result = true;
                }
                return result;
            })
        );
        if (Object.keys(missingRequiredValues).length > 0) setUserDetails({ ...userDetails, availableUserFieldsWithErrorsMessage });

        let missingEconomicName = false;
        if (userFields.includes('economicConnection') && newPersonDetails['economicConnection'] === 'Yes') {
            if (!newPersonDetails['employerName']) {
                missingEconomicName = true;
                availableUserFieldsWithErrorsMessage['employerName'].errorMessage = 'required.field';
                setUserDetails({ ...userDetails, availableUserFieldsWithErrorsMessage });
            }
        }

        const canProceed = Object.keys(missingRequiredValues).length <= 0 && !hasErrors && !missingEconomicName;

        if (canProceed) {
            newPersonDetails.economicConnection = newPersonDetails.economicConnection === 'Yes';
            if (updatedDetails.economicConnection) updatedDetails.economicConnection = newPersonDetails.economicConnection;
            const payload = { personDetails: { ...updatedDetails } };
            saveDetails(payload, 'UserDetails');
        }
    };

    const renderItems = (
        <>
            {Object.keys(userDetails)
                .filter((f) => !userDetails[f].noIterrate && userFields.includes(f))
                .map((keyName, idx) => {
                    const { label, placeholder, value, options, element, isLinkType, errorMessage, required = false } = userDetails[keyName];
                    let selectOptions = options || [];
                    const isCode = ['nationality', 'incorporationCountry', 'incorporationState'].includes(keyName);
                    if (keyName === 'incorporationState') {
                        const cntry = userDetails.incorporationCountry.value;
                        const countryStates = !!states && states.find((s) => s.countryCode === cntry);
                        selectOptions = (!!countryStates && countryStates.state) || [];
                    }
                    const commonProps = {
                        id: `${idx}_${keyName}`,
                        placeholder: t(placeholder),
                        name: keyName,
                        onChange: handleValueChange,
                        value: value || '',
                    };
                    const valueClass = !isLinkType || !value ? classes.fieldValue : classes.linkValue;
                    const diplayedValue = (isCode ? getCountryOrStateName(value, selectOptions || []) : value) || '-';
                    const inputOrDropdown =
                        element === 'Input' ? (
                            <Input
                                {...commonProps}
                                className={classes.textInput}
                                type="text"
                                error={!!errorMessage}
                                fullWidth
                                autoFocus={idx === 0}
                            />
                        ) : (
                            <Select
                                {...commonProps}
                                className={`${classes.textInput} ${classes.selectWidth}`}
                                displayEmpty
                                error={!!errorMessage}
                                IconComponent={DropDownArrow}
                                inputProps={{ 'aria-haspopup': 'listbox' }}>
                                <MenuItem value="" disabled className={classes.disabledMenuItem} role="option">
                                    {t(placeholder)}
                                </MenuItem>
                                {(selectOptions || []).map((option, idx) => (
                                    <MenuItem key={`${option.key}_${idx}`} value={option.code} role="option">
                                        {option.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        );

                    return (
                        <div key={`${keyName}_${idx}`}>
                            {readonly ? (
                                <React.Fragment>
                                    <Typography className={classes.fieldLabel}>
                                        {t(label)} {required && ' *'}
                                    </Typography>
                                    <Typography className={valueClass}>{diplayedValue}</Typography>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <InputLabel error={!!errorMessage} htmlFor={keyName} className={classes.fieldLabel}>
                                        {t(label)} {required && ' *'}
                                    </InputLabel>
                                    {inputOrDropdown}
                                    {!!errorMessage && <ErrorInput errorMessage={t(errorMessage)} classes={`${classes.inputError}`} />}
                                </React.Fragment>
                            )}
                        </div>
                    );
                })}
        </>
    );

    return (
        <Card
            title={'user.name.details.title'}
            canEdit={hasAdministratorRole}
            readonly={readonly}
            editCard={() => setReadonly(false)}
            saveCard={saveCard}
            cancelCard={cancelCard}
            cardId={'profile_user_details_card'}>
            {loading ? <CircularProgress /> : renderItems}
        </Card>
    );
};

export default UserDetailsCard;
